import { Badge } from '@finn/design-system/atoms/badge';
import { cn, parseToHtml } from '@finn/ui-utils';

import { Skeleton } from '../../../components/Skeleton';
import { useIsFeatureVisible } from '../../../hooks/useIsFeatureVisible';
import { useMathOperation, useValue } from '../../../hooks/useValue';
import { FieldPath, FormatConfig, VisibilityConfig } from '../../../types';
import {
  Item,
  useValueForDataItemBlock,
} from '../hooks/useValueForDataItemBlock';

export type DataItemBlockProps = {
  label?: string;
  badgeLabel?: string;
  bold?: boolean;
  note?: {
    label: string;
    value?: FieldPath;
    formatConfig?: FormatConfig;
    hidden?: VisibilityConfig;
  };
} & Item;

const getFontClasses = (item: DataItemBlockProps) => {
  let labelFontToUse = 'body-16-light';
  let valueFontToUse = 'body-16-light';
  if (item.bold) {
    labelFontToUse = 'body-16-regular';
    labelFontToUse = 'body-16-semibold';
  }
  if (item.variant === 'row') {
    labelFontToUse = 'body-14-regular';
    valueFontToUse = 'body-14-semibold';
  }
  if (item.variant === 'price' && item.badgeLabel) {
    labelFontToUse = 'body-16-regular';
    valueFontToUse = 'body-16-semibold';
  }

  return { labelFontToUse, valueFontToUse };
};

export const DataItemBlock = ({ item }: { item: DataItemBlockProps }) => {
  const { formattedValue, formattedOriginalValue, discount, status } =
    useValueForDataItemBlock(item);
  const [label] = useValue(item?.label || '');
  const note = item?.note;
  const isNoteHidden = useIsFeatureVisible(note?.hidden || {});
  const calculatedNoteValue = useMathOperation(note?.value) as string;
  const [noteValue] = useValue(calculatedNoteValue || '', note?.formatConfig);
  const noteLabel = isNoteHidden
    ? ''
    : note?.label?.replace('{value}', noteValue);
  const { labelFontToUse, valueFontToUse } = getFontClasses(item);

  return (
    <div
      className={cn('flex items-center justify-between gap-4', {
        'h-[50px] border-b border-pearl': item.variant === 'row',
      })}
    >
      {label ? (
        <div className="flex min-h-6 items-center gap-1.5">
          <p className={labelFontToUse}>{label}</p>
          {discount && discount > 0 && item?.badgeLabel ? (
            <Badge withStroke variant="monochromeBlue">
              {item.badgeLabel?.replace('{discount}', String(discount))}
            </Badge>
          ) : null}
        </div>
      ) : null}
      <div className="min-w-[33%]">
        <Skeleton
          loading={status === 'loading' || !formattedValue}
          fullWidth
          height="32px"
        >
          <div className="flex flex-col gap-2">
            <div className="flex gap-1">
              {discount && discount > 0 ? (
                <p className={cn('text-iron line-through', valueFontToUse)}>
                  {parseToHtml(String(formattedOriginalValue ?? ''))}
                </p>
              ) : null}
              <p
                className={cn(
                  valueFontToUse,
                  label ? 'w-full text-right' : 'w-full'
                )}
              >
                {parseToHtml(String(formattedValue ?? ''))}
              </p>
            </div>
            {noteLabel ? (
              <span className="text-right text-iron body-12-regular">
                {noteLabel}
              </span>
            ) : null}
          </div>
        </Skeleton>
      </div>
    </div>
  );
};
